/**
 * Genre Tag Mapping
 * Maps genre IDs to their corresponding search tags for the MetaAPI/SNAPI.
 * This ensures that when a user selects a genre, we search with the right keywords.
 */

interface GenreTagMap {
  tags: string[];
}

export const GENRE_TAG_MAPPING: Record<string, GenreTagMap> = {
  // --- GLOBAL MAIN GENRES ---
  pop: {
    tags: ["pop", "top 40", "hits", "charts", "popular music", "contemporary"],
  },
  rock: {
    tags: [
      "rock",
      "classic rock",
      "alternative",
      "indie",
      "hard rock",
      "rock music",
    ],
  },
  ac: {
    tags: ["adult contemporary", "easy listening", "soft rock", "ac", "mellow"],
  },
  hiphop: {
    tags: ["hip hop", "rap", "r&b", "rnb", "urban", "trap", "hip-hop"],
  },
  edm: {
    tags: ["electronic", "dance", "edm", "house", "techno", "trance", "club"],
  },
  country: {
    tags: ["country", "country music", "nashville", "americana", "bluegrass"],
  },
  latin: {
    tags: ["latin", "salsa", "reggaeton", "bachata", "merengue", "latin music"],
  },
  jazz: {
    tags: ["jazz", "blues", "soul", "funk", "smooth jazz", "bebop"],
  },
  classical: {
    tags: [
      "classical",
      "classical music",
      "opera",
      "symphony",
      "baroque",
      "orchestra",
    ],
  },
  reggae: {
    tags: ["reggae", "dub", "ska", "dancehall", "roots reggae"],
  },
  news: {
    tags: [
      "news",
      "talk",
      "current affairs",
      "politics",
      "news radio",
      "information",
    ],
  },
  sports: {
    tags: [
      "sports",
      "sport",
      "football",
      "soccer",
      "basketball",
      "hockey",
      "sports radio",
    ],
  },
  tech: {
    tags: ["tech", "technology", "science", "innovation", "gadgets", "ai"],
  },
  culture: {
    tags: [
      "culture",
      "society",
      "arts",
      "cultural",
      "humanities",
      "documentary",
    ],
  },

  // --- REGIONAL: SWEDEN (SE) ---
  "svensk-pop": {
    tags: [
      "svensk pop",
      "swedish pop",
      "pop",
      "sweden",
      "svenska",
      "svensk musik",
    ],
  },
  schlager: {
    tags: [
      "schlager",
      "dansband",
      "swedish schlager",
      "svenska schlager",
      "dansbands",
    ],
  },
  sport: {
    tags: ["sport", "idrott", "fotboll", "hockey", "svenska sport", "sports"],
  },

  // --- REGIONAL: UNITED STATES (US) ---
  // (Uses same IDs as global, so no additional mapping needed here)

  // --- FALLBACK/GENERIC ---
  musik: {
    tags: ["music", "musik", "various"],
  },
};

/**
 * Maps a genre ID to its corresponding search tags.
 * Returns the first tag as the primary search term.
 */
export const mapGenreToMetaFilters = (genreId: string): { tags: string[] } => {
  const mapping = GENRE_TAG_MAPPING[genreId];

  if (!mapping) {
    // Fallback: use the genreId itself as the tag
    return { tags: [genreId] };
  }

  return { tags: mapping.tags };
};
